// Astrea Ops Platform - Task board data-access layer (task_groups / tasks)
//
// Phase 3, sixth slice - the task board (project.groups[].items[]). Scoped down
// deliberately after scoping it out loud with Martin: the task board turned out to
// be a lot bigger and messier than Personnel was (~10 mutation call sites across
// ProjectBoard, an automatic reassignment cascade in autoAssignPOM, and several
// fields with no clean home in the Phase 1 schema - task comments are shaped
// differently than the `task_comments` table, there's a free-text "Blocked By"
// field with no column, and the special "Lead Checklist" item's nested `sections`
// structure has nothing in the schema to hold it). Martin's call: normalize only
// the core kanban fields that actually drive live multi-user sync on the board -
// group/task identity (name), status, assignee, and due date - onto the real
// `task_groups`/`tasks` tables from
// supabase/migrations/20260915000003_projects_and_lifecycle.sql. Everything else
// (notes, subtasks, comments, Blocked By, Lead Checklist sections) stays living
// only in `projects.app_data` for now, same bridge pattern as every other
// not-yet-normalized nested area, and gets its own follow-up slice later.
//
// Architecture note - this slice does NOT patch every mutation call site the way
// Personnel/assignments did. ProjectBoard's ~10 functions that touch
// project.groups (updateItem, updateStatus, doMassStatus, doMassAssign,
// updateDueDate, updateAssignee, addItem, the mark-all-done handler, and the
// autoAssignPOM auto-reassignment effect) all funnel through the SAME single prop,
// onUpdate, which is always the top-level updateProject function in App.jsx -
// already established as the one funnel every tab's edits pass through (see
// src/lib/projects.js's header). So instead of patching each of those ~10
// functions individually, syncTaskChanges() below is called once, centrally, from
// updateProject itself: it diffs the project's groups before and after the update
// and pushes only what actually changed to the real `tasks` table. This covers
// every current call site automatically, including ones added later, as long as
// they keep going through onUpdate - which every one of them already does.
//
// assigneeId: already a people.id uuid (same id convention people.js/
// assignments.js already established - `users` array items carry `id = people.id`),
// so no translation needed against tasks.assignee_id references people(id).
//
// status: the app's STATUSES ids (not_started/working/under_review/requested/
// received/done/not_required/stuck) match the task_status Postgres enum exactly.
//
// Ids: same convention as every other slice - a new group or task created
// client-side (project template creation, clone, "+ Add Task") must carry a real
// crypto.randomUUID(), not the app's own uid().

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function safeDate(s) {
  return typeof s === 'string' && ISO_DATE_RE.test(s) ? s : null;
}

// Loads every task_group + task row across all projects in two queries and groups
// them by project_id, ready for projects.js's loadProjects() to overlay via
// mergeTaskGroups() below. Mirrors the "one query, group client-side" shape
// unavailability.js/assignments.js already use.
export async function loadTaskGroupsByProject() {
  const [{ data: groupRows, error: gErr }, { data: taskRows, error: tErr }] = await Promise.all([
    // v7.77: every row, page by page (more than 1000 tasks hid all but the first of each group)
    selectAllRes(() => supabase.from('task_groups').select('*').order('sort_order').order('id')),
    selectAllRes(() => supabase.from('tasks').select('*').order('sort_order').order('id')),
  ]);
  if (gErr) throw gErr;
  if (tErr) throw tErr;
  const tasksByGroup = new Map();
  (taskRows || []).forEach(row => {
    const list = tasksByGroup.get(row.task_group_id) || [];
    list.push({
      id: row.id,
      name: row.name,
      status: row.status,
      assigneeId: row.assignee_id || '',
      dueDate: row.due_date || '',
    });
    tasksByGroup.set(row.task_group_id, list);
  });
  const byProject = new Map();
  (groupRows || []).forEach(row => {
    const list = byProject.get(row.project_id) || [];
    list.push({
      id: row.id,
      name: row.name,
      color: row.color || '',
      items: tasksByGroup.get(row.id) || [],
    });
    byProject.set(row.project_id, list);
  });
  return byProject;
}

// Merges the authoritative real groups/tasks onto a project's app_data-sourced
// groups array. The real rows decide WHICH groups/tasks exist and their core
// fields (name, color, status, assigneeId, dueDate); everything not yet
// normalized (notes, subtasks, comments, blockedBy, isLeadChecklist, sections,
// autoDate) comes from the matching app_data item, found by id, defaulted when
// missing (a task created by someone else since this slice landed has nothing in
// this browser's app_data yet). Falls back to the app_data groups untouched for a
// project with no real task_groups rows yet (created before this slice shipped).
export function mergeTaskGroups(appDataGroups, realGroups) {
  if (!realGroups || realGroups.length === 0) return appDataGroups || [];
  const appItemsById = new Map();
  (appDataGroups || []).forEach(g => (g.items || []).forEach(i => appItemsById.set(i.id, i)));
  return realGroups.map(rg => ({
    id: rg.id,
    name: rg.name,
    color: rg.color,
    items: rg.items.map(ri => {
      const extra = appItemsById.get(ri.id) || {};
      return { notes: '', subtasks: [], comments: [], blockedBy: '', ...extra, ...ri };
    }),
  }));
}

// Bulk-creates a brand-new project's initial groups+tasks - called once from
// project creation (template, silent add, new-project modal, clone), not from
// ongoing edits. Two batch inserts rather than one row at a time.
export async function taskGroupsBulkCreate(projectId, groups) {
  if (!groups || groups.length === 0) return;
  const groupRows = groups.map((g, gi) => ({
    id: g.id,
    project_id: projectId,
    name: g.name || '(untitled group)',
    color: g.color || null,
    sort_order: gi,
  }));
  const { error: gErr } = await supabase.from('task_groups').insert(groupRows);
  if (gErr) throw gErr;
  const taskRows = [];
  groups.forEach(g => {
    (g.items || []).forEach((item, ii) => {
      taskRows.push({
        id: item.id,
        task_group_id: g.id,
        name: item.name || '(untitled task)',
        status: item.status || 'not_started',
        assignee_id: item.assigneeId || null,
        due_date: safeDate(item.dueDate),
        sort_order: ii,
      });
    });
  });
  if (taskRows.length > 0) {
    const { error: tErr } = await supabase.from('tasks').insert(taskRows);
    if (tErr) throw tErr;
  }
}

// entry must already carry a real UUID id.
export async function taskCreate(item, groupId, sortOrder = 0) {
  const { error } = await supabase.from('tasks').insert({
    id: item.id,
    task_group_id: groupId,
    name: item.name || '(untitled task)',
    status: item.status || 'not_started',
    assignee_id: item.assigneeId || null,
    due_date: safeDate(item.dueDate),
    sort_order: sortOrder,
  });
  if (error) throw error;
}

export async function taskUpdate(id, patch) {
  const row = {};
  if ('name' in patch) row.name = patch.name;
  if ('status' in patch) row.status = patch.status;
  if ('assigneeId' in patch) row.assignee_id = patch.assigneeId || null;
  if ('dueDate' in patch) row.due_date = safeDate(patch.dueDate);
  if ('sortOrder' in patch) row.sort_order = patch.sortOrder;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase.from('tasks').update(row).eq('id', id);
  if (error) throw error;
}

// Diffs an existing project's groups before and after an update and pushes only
// the core fields that changed - see the module header for why this is called
// once, centrally, from updateProject rather than patched into every ProjectBoard
// function that touches project.groups. A task id present in nextGroups but not
// prevGroups is a brand-new task (e.g. "+ Add Task") and gets created instead.
// 1 Oct 2026 (Tasks redesign): tasks can now be deleted, so a task present before but
// gone after is deleted here too (otherwise the real row would bring it back on the
// next load), and a task whose position in its group changed gets its sort_order updated.
export async function taskDelete(id) {
  const { error } = await supabase.from('tasks').delete().eq('id', id);
  if (error) throw error;
}

// v7.78 (17 Oct 2026): a prospect made a full project gets a whole new task list. Its old groups
// are removed first (the database removes their tasks with them), then the new list is added.
export async function taskBoardReplace(projectId, groups) {
  const { error } = await supabase.from('task_groups').delete().eq('project_id', projectId);
  if (error) throw error;
  await taskGroupsBulkCreate(projectId, groups);
}

export async function syncTaskChanges(prevGroups, nextGroups) {
  const prevById = new Map();
  const prevIndex = new Map();
  (prevGroups || []).forEach(g => (g.items || []).forEach((i, ii) => { prevById.set(i.id, i); prevIndex.set(i.id, ii); }));
  const nextIds = new Set();
  (nextGroups || []).forEach(g => (g.items || []).forEach(i => nextIds.add(i.id)));
  const jobs = [];
  prevById.forEach((_, id) => { if (!nextIds.has(id)) jobs.push(taskDelete(id)); });
  (nextGroups || []).forEach(g => {
    (g.items || []).forEach((item, ii) => {
      const prev = prevById.get(item.id);
      if (!prev) {
        jobs.push(taskCreate(item, g.id, ii));
        return;
      }
      const patch = {};
      if (prevIndex.get(item.id) !== ii) patch.sortOrder = ii;
      if (item.name !== prev.name) patch.name = item.name;
      if (item.status !== prev.status) patch.status = item.status;
      if ((item.assigneeId || '') !== (prev.assigneeId || '')) patch.assigneeId = item.assigneeId;
      if ((item.dueDate || '') !== (prev.dueDate || '')) patch.dueDate = item.dueDate;
      if (Object.keys(patch).length > 0) jobs.push(taskUpdate(item.id, patch));
    });
  });
  await Promise.all(jobs);
}
